// Lightweight helpers to export AI-generated docs (tailored resume / cover letter).
import { logError } from "./logger";
// The AI returns Markdown, so we render a minimal-but-clean HTML for print-to-PDF,
// and also support a plain-text (.txt) download. No external markdown dependency.

const esc = (s = "") =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const inline = (s = "") =>
  esc(s)
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2">$1</a>')
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/__([^_]+)__/g, "<strong>$1</strong>")
    .replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>")
    .replace(/`([^`]+)`/g, "<code>$1</code>");

// Minimal Markdown → HTML: headings, hr, unordered/ordered lists, paragraphs.
function mdToHtml(md = "") {
  const lines = String(md).replace(/\r\n/g, "\n").split("\n");
  const out = [];
  let list = null; // "ul" | "ol" | null
  const closeList = () => {
    if (list) { out.push(`</${list}>`); list = null; }
  };

  for (const raw of lines) {
    const line = raw.replace(/\s+$/, "");
    if (!line.trim()) { closeList(); continue; }

    const h = line.match(/^(#{1,4})\s+(.*)$/);
    if (h) { closeList(); const lvl = h[1].length; out.push(`<h${lvl}>${inline(h[2])}</h${lvl}>`); continue; }

    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line.trim())) { closeList(); out.push("<hr/>"); continue; }

    const ul = line.match(/^\s*[-*+]\s+(.*)$/);
    if (ul) { if (list !== "ul") { closeList(); out.push("<ul>"); list = "ul"; } out.push(`<li>${inline(ul[1])}</li>`); continue; }

    const ol = line.match(/^\s*\d+[.)]\s+(.*)$/);
    if (ol) { if (list !== "ol") { closeList(); out.push("<ol>"); list = "ol"; } out.push(`<li>${inline(ol[1])}</li>`); continue; }

    closeList();
    out.push(`<p>${inline(line)}</p>`);
  }
  closeList();
  return out.join("\n");
}

function buildDocHtml(title, markdown) {
  return `<!doctype html>
<html>
<head>
<meta charset="utf-8"/>
<title>${esc(title)}</title>
<style>
  @page { size: A4; margin: 18mm; }
  * { box-sizing: border-box; }
  body { font-family: Georgia, "Times New Roman", serif; color: #18181b; line-height: 1.5; margin: 0; }
  h1 { font-family: Arial, Helvetica, sans-serif; font-size: 22px; margin: 0 0 12px; letter-spacing: -0.4px; }
  h2 { font-family: Arial, Helvetica, sans-serif; font-size: 15px; margin: 18px 0 6px; }
  h3, h4 { font-family: Arial, Helvetica, sans-serif; font-size: 13px; margin: 14px 0 4px; }
  p { font-size: 12.5px; margin: 0 0 8px; }
  ul, ol { font-size: 12.5px; margin: 0 0 10px; padding-left: 22px; }
  li { margin-bottom: 3px; }
  hr { border: 0; border-top: 1px solid #d4d4d8; margin: 14px 0; }
  a { color: #18181b; }
  code { font-family: "JetBrains Mono", monospace; font-size: 11.5px; }
</style>
</head>
<body>
${mdToHtml(markdown)}
</body>
</html>`;
}

// Print arbitrary Markdown content to PDF via a hidden iframe (no popup dependency).
export function printDoc(title, markdown, onError) {
  const html = buildDocHtml(title, markdown);
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.setAttribute("title", "doc-print");
  iframe.srcdoc = html;
  iframe.onload = () => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (err) {
      logError("Print failed:", err);
      if (onError) onError(err);
    }
    setTimeout(() => iframe.remove(), 1000);
  };
  document.body.appendChild(iframe);
}

// Download a string as a .txt file.
export function downloadText(filename, text) {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Build a filesystem-safe slug from job title/company for download filenames.
export function docSlug(...parts) {
  return parts
    .filter(Boolean)
    .join("-")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60) || "document";
}
